import { Button, Card, CardContent, Grid, Typography } from '@mui/material'
import { SimpleImageCard } from 'components/ImageCard/SimpleImageCard'
import { type Resource } from 'src/types/api'

const normalizeResourceUrl = (url: string | null | undefined) => {
  if (!url) {
    return undefined
  }

  const trimmedUrl = url.trim()
  if (trimmedUrl === '') {
    return undefined
  }

  return /^[a-z][a-z\d+.-]*:/i.test(trimmedUrl)
    ? trimmedUrl
    : `https://${trimmedUrl}`
}

export const ResourceCard = ({ resource }: { resource: Resource }) => {
  const externalUrl = normalizeResourceUrl(resource.url)
  const hasExternalUrl = Boolean(externalUrl)
  const downloadUrl = resource.publicFileUrl
  const hasDownloadUrl = Boolean(downloadUrl)

  const handleButtonClick = async () => {
    if (externalUrl) {
      window.open(externalUrl, '_blank', 'noopener,noreferrer')
      return
    }

    if (!downloadUrl) {
      return
    }

    try {
      const response = await fetch(downloadUrl)
      if (!response.ok) {
        throw new Error(`Failed to download resource: ${response.status}`)
      }

      const blob = await response.blob()
      const objectUrl = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = objectUrl
      link.download = resource.fileName ?? 'resource'
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(objectUrl)
    } catch {
      window.open(downloadUrl, '_blank', 'noopener,noreferrer')
    }
  }

  return (
    <Card className="mb-10">
      <CardContent>
        <Grid
          container
          spacing={2}
          direction={{ xs: 'column', sm: 'row' }}
          sx={{ alignItems: { xs: 'stretch', sm: 'flex-start' } }}
        >
          <Grid
            item
            sx={{
              display: 'flex',
              justifyContent: { xs: 'center', sm: 'flex-start' },
              flexShrink: 0,
            }}
          >
            <SimpleImageCard
              imageSource={resource.publicImageUrl}
              sx={{ width: '140px', height: '140px' }}
              fallbackContent={
                <span
                  aria-label="file-icon"
                  style={{
                    fontSize: 75,
                    height: '140px',
                    width: '140px',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  📄
                </span>
              }
            />
          </Grid>
          <Grid item sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="h2" sx={{ marginBottom: '15px' }}>
              {resource.title}
            </Typography>
            <Typography
              variant="body1"
              sx={{ ml: '5px', marginBottom: '15px' }}
            >
              {resource.description}
            </Typography>
            <Button
              variant="contained"
              onClick={handleButtonClick}
              disabled={!hasExternalUrl && !hasDownloadUrl}
            >
              <Typography>
                {hasExternalUrl ? 'Click to View' : 'Download'}
              </Typography>
            </Button>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  )
}
